/**
 * Shared cell renderers for the customer segment tables. Mirrors the legacy
 * `customer-list-shared-config` intent (name link, follower join, industry,
 * status pill) but built on `@vnetwork/ui` primitives + `--vn-*` tokens.
 */
import type { ReactNode } from 'react';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import { TableLink } from '@/components/table-link';
import { EntityLink } from '@/components/entity-link';
import type { CustomerFollowerName, CustomerListRow } from '@/api/customers';
import './customer-cells.css';

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/**
 * Name cell — links to the customer detail as `{id} – {name}` via EntityLink
 * (single line, truncated). Keyboard-focusable.
 */
export function nameCell(row: CustomerListRow): ReactNode {
  const name = row.company_name || row.customer_name;
  return (
    <EntityLink
      to={`/customers/${row.customer_id}`}
      id={row.customer_id}
      name={name}
    />
  );
}

/** Plain `customer_id` link (Deleted "ID" column) — no uppercase name label. */
export function idCell(row: CustomerListRow): ReactNode {
  return (
    <TableLink to={`/customers/${row.customer_id}`}>
      {row.customer_id}
    </TableLink>
  );
}

/**
 * Follower names joined with ", ". The API returns the enriched objects under
 * either the `*_followers` or the `*_follower_names` key depending on segment —
 * read both. Empty → an italic muted fallback.
 */
export function followerNames(
  primary: CustomerFollowerName[] | undefined,
  fallback: CustomerFollowerName[] | undefined,
  emptyLabel: string,
): ReactNode {
  const list = primary?.length ? primary : fallback;
  const names = (list ?? [])
    .map((f) => f?.employee_name)
    .filter(Boolean)
    .join(', ');
  if (!names) return <span style={muted}>{emptyLabel}</span>;
  return (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={names}>
      {names}
    </span>
  );
}

/* ── Journey (Qualified Prospects / Cool Lead) accessors ──────────────────────
 * Journey rows nest followers + service interests under
 * `customer_journey[].customer_journey_explaination`, not the flat `*_followers`
 * / `customer_service_interestings` keys the active/trial segments use. These
 * fields are absent from the shared `CustomerListRow` type, so read them via a
 * narrowed loose view of the row. */

interface JourneyExplaination {
  sale_tracking_id?: { employee_id?: number; employee_name?: string }[];
  service_interestings?: string[];
}
interface JourneyEntry {
  customer_journey_explaination?: JourneyExplaination;
}

function journeyEntries(row: CustomerListRow): JourneyEntry[] {
  const list = (row as { customer_journey?: unknown }).customer_journey;
  return Array.isArray(list) ? (list as JourneyEntry[]) : [];
}

/** Unique sales-follower names across the row's journey, deduped by employee_id. */
export function journeyFollowersCell(row: CustomerListRow): ReactNode {
  const seen = new Map<string, string>();
  for (const entry of journeyEntries(row)) {
    for (const t of entry.customer_journey_explaination?.sale_tracking_id ?? []) {
      const name = t?.employee_name?.trim();
      if (!name) continue;
      const key = t?.employee_id != null ? String(t.employee_id) : name;
      if (!seen.has(key)) seen.set(key, name);
    }
  }
  const names = Array.from(seen.values()).join(', ');
  if (!names) return <span style={muted}>Unassigned</span>;
  return (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={names}>
      {names}
    </span>
  );
}

/** Unique service-interest values across the row's journey. */
export function journeyInterestsCell(row: CustomerListRow): ReactNode {
  const set = new Set<string>();
  for (const entry of journeyEntries(row)) {
    for (const s of entry.customer_journey_explaination?.service_interestings ?? []) {
      const v = (s ?? '').trim();
      if (v) set.add(v);
    }
  }
  return joinList(Array.from(set), 'None');
}

/** Industry — capitalized; `-1`/empty → an italic muted "Unknown". */
export function industryCell(value: string | undefined): ReactNode {
  if (!value || value === '-1') return <span style={muted}>Unknown</span>;
  return <span style={{ textTransform: 'capitalize' }}>{value}</span>;
}

/** Join a string list with ", "; empty → italic muted fallback. */
export function joinList(values: string[] | undefined, emptyLabel: string): ReactNode {
  const text = (values ?? []).filter(Boolean).join(', ');
  if (!text) return <span style={muted}>{emptyLabel}</span>;
  return (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={text}>
      {text}
    </span>
  );
}

/** Two-line clamped free text (deletion reason). */
export function clampText(value: string | undefined | null, lines = 2): ReactNode {
  const text = value || '—';
  return (
    <span
      style={{
        display: '-webkit-box',
        WebkitLineClamp: lines,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}
      title={value || ''}
    >
      {text}
    </span>
  );
}

/** Muted, strikethrough email (deleted rows). */
export function struckEmail(value: string | undefined): ReactNode {
  return (
    <span style={{ ...muted, textDecoration: 'line-through' }}>{value || '—'}</span>
  );
}

/** Map a customer status slug → a StatusPill tone. */
export function statusTone(status: string | undefined): StatusTone {
  const s = (status || '').toLowerCase();
  if (s.includes('active') || s.includes('customer')) return 'success';
  if (s.includes('trial')) return 'info';
  if (s.includes('cancel') || s.includes('stop') || s.includes('dead') || s.includes('delete')) return 'danger';
  if (s.includes('cool') || s.includes('prospect') || s.includes('lead')) return 'warning';
  return 'neutral';
}

/** Status cell as a StatusPill. */
export function statusCell(status: string | undefined): ReactNode {
  return <StatusPill tone={statusTone(status)}>{status || '—'}</StatusPill>;
}
